/* DEV-431 The Washed Linen Blend guide.
   Solids: one shared state (solid + color) drives the three edge cards, the
   buy panel (photo, color, piece, size, a duvet insert when the piece is a
   duvet cover, a $5 swatch) and the finishing pieces for that bed, which
   start in the same color and the matching size.
   Prints: tapping a print opens its buy panel directly under that print's
   row (the grid stays where it is), with its matching pieces. Filters show
   one family of prints at a time.
   Swatches: any solid color for the swatch price, or the bundle.
   Every add goes through /cart/add.js, the button reads "Added" at once and
   the site's cart drawer opens (window.HEACart), so there is always a cue.
   Links can open the page on a choice: ?solid=luna&color=Slate opens Luna in
   Slate at the solids; ?print=provence opens Provence's panel (handle or name).
   Markup: sections/linen-blend-guide.liquid. */
(function () {
  'use strict';

  var root = document.querySelector('[data-linen-blend-guide]');
  if (!root) return;
  var dataNode = root.querySelector('[data-lbg-data]');
  if (!dataNode) return;
  var data;
  try { data = JSON.parse(dataNode.textContent); } catch (error) { return; }

  var OPTION_LABELS = { 'product': 'Piece', 'size': 'Size' };
  var params;
  try { params = new URLSearchParams(window.location.search); } catch (error) { params = { get: function () { return null; } }; }

  /* Sizes that fit one another: a King/Cal King bed takes the King bed skirt,
     King shams and a King/Cal King insert; Full/Queen takes Queen pieces and
     Standard shams. Combined names first, then the parts. */
  var SIZE_FIT = {
    'king/cal king': ['king/cal king', 'king', 'cal king'],
    'king': ['king', 'king/cal king'],
    'cal king': ['cal king', 'king/cal king', 'king'],
    'full/queen': ['full/queen', 'queen', 'full', 'standard'],
    'queen': ['queen', 'full/queen', 'standard'],
    'full': ['full', 'full/queen', 'standard'],
    'twin xl': ['twin xl', 'twin/twin xl', 'twin', 'standard'],
    'twin': ['twin', 'twin/twin xl', 'standard']
  };

  /* ---------- Helpers ---------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function money(cents) {
    var whole = cents % 100 === 0;
    var value = (cents / 100).toFixed(whole ? 0 : 2);
    return '$' + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function colorNames(product) {
    return (product.order || []).filter(function (name) { return product.colors[name]; });
  }

  function hasColors(product) {
    var names = colorNames(product);
    return !(names.length === 1 && names[0] === '');
  }

  function firstAvailableColor(product, wanted) {
    if (wanted != null && product.colors[wanted] && product.colors[wanted].available) return wanted;
    var names = colorNames(product);
    for (var i = 0; i < names.length; i++) {
      if (product.colors[names[i]].available) return names[i];
    }
    return names[0];
  }

  function sizeOf(opts) {
    for (var i = 0; i < opts.length; i++) {
      if (SIZE_FIT[String(opts[i]).toLowerCase()]) return String(opts[i]).toLowerCase();
    }
    return null;
  }

  function swapImage(img, entry, eager) {
    if (!img || !entry) return;
    if (img.getAttribute('data-src') === entry.src) return;
    if (eager) img.loading = 'eager';
    img.removeAttribute('srcset');
    if (entry.srcset) img.srcset = entry.srcset;
    img.src = entry.src;
    img.alt = entry.alt || '';
    img.setAttribute('data-src', entry.src);
  }

  /* ---------- Cart ---------- */

  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().then(function (body) {
          throw new Error(body.description || body.message || 'We could not add that to your cart.');
        }, function () { throw new Error('We could not add that to your cart.'); });
      }
      return response.json();
    }).then(function (result) {
      var cart = window.HEACart;
      if (cart && typeof cart.refresh === 'function') {
        return cart.refresh().then(function () {
          if (typeof cart.open === 'function') cart.open();
          return result;
        });
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return result;
    });
  }

  function runAdd(button, status, items, doneText) {
    if (!items.length || button.disabled) return;
    var label = button.getAttribute('data-label') || button.textContent;
    button.setAttribute('data-label', label);
    button.disabled = true;
    button.textContent = 'Adding…';
    if (status) status.textContent = '';
    addToCart(items).then(function () {
      button.textContent = 'Added';
      button.classList.add('is-added');
      if (status) status.textContent = doneText || 'Added to your cart.';
      window.setTimeout(function () {
        button.textContent = label;
        button.classList.remove('is-added');
        button.disabled = false;
      }, 1800);
    }).catch(function (error) {
      button.textContent = label;
      button.disabled = false;
      if (status) status.textContent = error.message;
    });
  }

  /* ---------- Color chips ---------- */

  function renderChips(container, product, current, mode, onPick, usePhotos) {
    container.innerHTML = '';
    colorNames(product).forEach(function (name) {
      var entry = product.colors[name];
      var chip = el('button', mode === 'pill' ? 'lbg-pill' : 'lbg-chip');
      chip.type = 'button';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('aria-checked', name === current ? 'true' : 'false');
      chip.setAttribute('aria-label', name + (entry.available ? '' : ', sold out'));
      chip.title = name;
      if (name === current) chip.classList.add('is-selected');
      if (!entry.available) chip.classList.add('is-sold-out');
      if (mode === 'pill') {
        chip.textContent = name;
      } else if (!usePhotos && data.chips && data.chips[name]) {
        chip.style.backgroundImage = 'url("' + data.chips[name] + '")';
      } else {
        chip.style.backgroundImage = 'url("' + entry.thumb + '")';
      }
      chip.addEventListener('click', function () { onPick(name); });
      container.appendChild(chip);
    });
  }

  /* ---------- A buy panel ---------- */

  function Stage(node, options) {
    this.node = node;
    this.options = options || {};
    this.image = node.querySelector('[data-stage-image]');
    this.name = node.querySelector('[data-stage-name]');
    this.line = node.querySelector('[data-stage-line]');
    this.price = node.querySelector('[data-stage-price]');
    this.colorsWrap = node.querySelector('[data-stage-colors-wrap]');
    this.colorLabel = node.querySelector('[data-stage-color]');
    this.chips = node.querySelector('[data-stage-chips]');
    this.optionsWrap = node.querySelector('[data-stage-options]');
    this.addon = node.querySelector('[data-stage-addon]');
    this.add = node.querySelector('[data-stage-add]');
    this.status = node.querySelector('[data-stage-status]');
    this.link = node.querySelector('[data-stage-link]');
    this.swatch = node.querySelector('[data-stage-swatch]');
    this.selection = [];
    this.insertOn = false;
    this.insertId = null;
    var self = this;
    if (this.add) this.add.addEventListener('click', function () { self.buy(); });
    if (this.swatch) this.swatch.addEventListener('click', function () { self.buySwatch(); });
  }

  Stage.prototype.show = function (product, color, title, line, eager) {
    this.product = product;
    this.title = title || product.title;
    this.color = firstAvailableColor(product, color);
    this.lineText = line || '';
    this.status && (this.status.textContent = '');
    this.pickSelection();
    this.render(eager);
  };

  Stage.prototype.variants = function () {
    var entry = this.product.colors[this.color];
    return entry ? entry.variants : [];
  };

  /* Keep the piece and size already chosen when they exist in the new color
     or product; otherwise the first combination in stock. */
  Stage.prototype.pickSelection = function () {
    var variants = this.variants();
    var previous = this.selection || [];
    var match = null;
    if (previous.length) {
      match = variants.filter(function (v) {
        return v.available && v.opts.every(function (value, i) { return value === previous[i]; });
      })[0];
    }
    if (!match && previous.length) {
      var size = sizeOf(previous);
      if (size) {
        match = variants.filter(function (v) {
          return v.available && v.opts.some(function (value) { return String(value).toLowerCase() === size; }) && v.opts[0] === previous[0];
        })[0] || variants.filter(function (v) {
          return v.available && v.opts.some(function (value) { return String(value).toLowerCase() === size; });
        })[0];
      }
    }
    if (!match) match = variants.filter(function (v) { return v.available; })[0] || variants[0];
    this.selection = match ? match.opts.slice() : [];
  };

  Stage.prototype.current = function () {
    var selection = this.selection;
    return this.variants().filter(function (v) {
      return v.opts.every(function (value, i) { return value === selection[i]; });
    })[0] || null;
  };

  Stage.prototype.choose = function (index, value) {
    var variants = this.variants();
    var next = this.selection.slice();
    next[index] = value;
    var exact = variants.filter(function (v) {
      return v.opts.every(function (o, i) { return o === next[i]; });
    })[0];
    if (!exact || !exact.available) {
      var fallback = variants.filter(function (v) { return v.available && v.opts[index] === value; })[0];
      if (fallback) next = fallback.opts.slice();
    }
    this.selection = next;
    this.renderOptions();
    this.renderBuy();
    if (this.options.onSelection) this.options.onSelection(this);
  };

  Stage.prototype.render = function (eager) {
    var entry = this.product.colors[this.color];
    swapImage(this.image, entry, eager);
    this.name.textContent = this.title;
    if (this.line) this.line.textContent = this.lineText;
    var self = this;
    if (hasColors(this.product)) {
      this.colorsWrap.hidden = false;
      this.colorLabel.textContent = this.color;
      renderChips(this.chips, this.product, this.color, this.options.chipMode, function (name) {
        if (name === self.color) return;
        self.color = name;
        self.pickSelection();
        self.render(true);
        if (self.options.onColor) self.options.onColor(name, self);
      });
    } else {
      this.colorsWrap.hidden = true;
    }
    this.renderOptions();
    this.renderBuy();
    if (this.link) this.link.href = this.product.url;
    if (this.swatch) {
      var swatch = data.swatch && data.swatch.colors[this.color];
      this.swatch.hidden = !swatch || !swatch.available;
      this.swatch.textContent = 'Order a ' + (data.swatchFrom || '') + ' Swatch in ' + this.color;
    }
  };

  Stage.prototype.renderOptions = function () {
    var wrap = this.optionsWrap;
    wrap.innerHTML = '';
    var variants = this.variants();
    var names = this.product.options || [];
    var selection = this.selection;
    var self = this;
    names.forEach(function (optionName, index) {
      var values = [];
      variants.forEach(function (v) { if (values.indexOf(v.opts[index]) === -1) values.push(v.opts[index]); });
      if (values.length < 2 && names.length > 1) return;
      var group = el('div', 'lbg-stage__group');
      var label = el('p', 'lbg-stage__label', (OPTION_LABELS[optionName.toLowerCase()] || optionName) + ': ');
      label.appendChild(el('span', 'lbg-stage__value', selection[index] || ''));
      group.appendChild(label);
      var row = el('div', 'lbg-options');
      row.setAttribute('role', 'radiogroup');
      row.setAttribute('aria-label', OPTION_LABELS[optionName.toLowerCase()] || optionName);
      values.forEach(function (value) {
        var inStock = variants.some(function (v) {
          return v.available && v.opts[index] === value && v.opts.every(function (o, i) { return i === index || o === selection[i]; });
        });
        var anyStock = variants.some(function (v) { return v.available && v.opts[index] === value; });
        var button = el('button', 'lbg-option', value);
        button.type = 'button';
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', value === selection[index] ? 'true' : 'false');
        if (value === selection[index]) button.classList.add('is-selected');
        if (!inStock) {
          button.classList.add('is-sold-out');
          button.setAttribute('aria-label', value + ', sold out');
        }
        if (!anyStock) button.disabled = true;
        button.addEventListener('click', function () { self.choose(index, value); });
        row.appendChild(button);
      });
      group.appendChild(row);
      wrap.appendChild(group);
    });
  };

  /* A duvet cover needs an insert: offer one in the matching size. */
  Stage.prototype.insertChoices = function (variant) {
    if (!variant || !data.inserts || !data.inserts.length) return [];
    var needsInsert = variant.opts.some(function (value) { return /duvet cover/i.test(value); });
    if (!needsInsert) return [];
    var size = sizeOf(variant.opts);
    if (!size) return [];
    var fits = (SIZE_FIT[size] || [size]).slice();
    if (size === 'full/queen') fits = ['queen', 'full/queen', 'full'];
    if (size === 'twin xl') fits = ['twin xl', 'twin'];
    var choices = [];
    data.inserts.forEach(function (insert) {
      var entry = insert.colors[''] || insert.colors[colorNames(insert)[0]];
      if (!entry) return;
      for (var f = 0; f < fits.length; f++) {
        var found = entry.variants.filter(function (v) {
          return v.available && v.opts.some(function (value) { return String(value).toLowerCase() === fits[f]; });
        })[0];
        if (found) {
          choices.push({ id: found.id, cents: found.cents, label: insert.title + ', ' + found.opts.join(' · ') });
          break;
        }
      }
    });
    return choices;
  };

  Stage.prototype.renderAddon = function (variant) {
    var addon = this.addon;
    if (!addon) return;
    var choices = this.insertChoices(variant);
    if (!choices.length) {
      addon.hidden = true;
      addon.innerHTML = '';
      this.insertOn = false;
      return;
    }
    var self = this;
    if (!choices.some(function (c) { return c.id === self.insertId; })) this.insertId = choices[0].id;
    addon.hidden = false;
    addon.innerHTML = '';
    var labelNode = el('label', 'lbg-addon__toggle');
    var box = el('input', 'lbg-addon__check');
    box.type = 'checkbox';
    box.checked = this.insertOn;
    labelNode.appendChild(box);
    labelNode.appendChild(el('span', 'lbg-addon__text', 'Add a duvet insert'));
    addon.appendChild(labelNode);
    var select = el('select', 'lbg-select lbg-addon__select');
    select.setAttribute('aria-label', 'Duvet insert');
    choices.forEach(function (choice) {
      var option = el('option', null, choice.label + ' · ' + money(choice.cents));
      option.value = String(choice.id);
      if (choice.id === self.insertId) option.selected = true;
      select.appendChild(option);
    });
    select.disabled = !this.insertOn;
    addon.appendChild(select);
    box.addEventListener('change', function () {
      self.insertOn = box.checked;
      select.disabled = !box.checked;
      self.renderBuy(true);
    });
    select.addEventListener('change', function () {
      self.insertId = Number(select.value);
      self.renderBuy(true);
    });
  };

  Stage.prototype.renderBuy = function (keepAddon) {
    var variant = this.current();
    if (!keepAddon) this.renderAddon(variant);
    if (!variant) {
      this.price.textContent = '';
      this.add.disabled = true;
      this.add.textContent = 'Sold Out';
      return;
    }
    var total = variant.cents;
    var insert = null;
    if (this.insertOn && this.insertId) {
      insert = this.insertChoices(variant).filter(function (c) { return c.id === this.insertId; }, this)[0];
      if (insert) total += insert.cents;
    }
    this.price.textContent = money(variant.cents) + (insert ? ' + insert ' + money(insert.cents) : '');
    this.add.removeAttribute('data-label');
    if (!variant.available) {
      this.add.disabled = true;
      this.add.textContent = 'Sold Out';
    } else {
      this.add.disabled = false;
      this.add.textContent = insert ? 'Add Both to Cart · ' + money(total) : 'Add to Cart';
    }
    if (this.link) this.link.href = this.product.url + '?variant=' + variant.id;
  };

  Stage.prototype.buy = function () {
    var variant = this.current();
    if (!variant || !variant.available) return;
    var items = [{ id: variant.id, quantity: 1 }];
    if (this.insertOn && this.insertId) items.push({ id: this.insertId, quantity: 1 });
    runAdd(this.add, this.status, items);
  };

  Stage.prototype.buySwatch = function () {
    var swatch = data.swatch && data.swatch.colors[this.color];
    if (!swatch) return;
    var variant = swatch.variants.filter(function (v) { return v.available; })[0];
    if (!variant) return;
    runAdd(this.swatch, this.status, [{ id: variant.id, quantity: 1 }], 'A ' + this.color + ' swatch is in your cart.');
  };

  /* ---------- Finishing pieces ---------- */

  /* usePhotos: chips show each color's own product photo (print pieces),
     not the solid fabric swatch that shares the color name. */
  function renderRows(container, pieces, color, size, usePhotos) {
    container.innerHTML = '';
    var shown = 0;
    pieces.forEach(function (piece) {
      var product = piece.product;
      if (!product || !colorNames(product).length) return;
      var anyStock = colorNames(product).some(function (name) { return product.colors[name].available; });
      if (!anyStock) return;
      shown += 1;
      var state = { color: firstAvailableColor(product, color), variant: null, qty: piece.qty || 1 };

      var row = el('div', 'lbg-row');
      var thumb = el('img', 'lbg-row__image');
      thumb.width = 120;
      thumb.height = 120;
      thumb.loading = 'lazy';
      thumb.alt = '';
      var media = el('a', 'lbg-row__media');
      media.href = product.url;
      media.setAttribute('tabindex', '-1');
      media.setAttribute('aria-hidden', 'true');
      media.appendChild(thumb);
      row.appendChild(media);

      var body = el('div', 'lbg-row__body');
      var name = el('a', 'lbg-row__name', piece.name || product.title);
      name.href = product.url;
      body.appendChild(name);
      if (piece.note) body.appendChild(el('p', 'lbg-row__note', piece.note));
      var chips = null;
      if (hasColors(product) && colorNames(product).length > 1) {
        chips = el('div', 'lbg-chips lbg-chips--small');
        chips.setAttribute('role', 'radiogroup');
        chips.setAttribute('aria-label', (piece.name || product.title) + ' color');
        body.appendChild(chips);
      }
      var controls = el('div', 'lbg-row__controls');
      var select = el('select', 'lbg-select lbg-row__select');
      select.setAttribute('aria-label', (piece.name || product.title) + ' size');
      var qty = el('select', 'lbg-select lbg-row__qty');
      qty.setAttribute('aria-label', 'Quantity');
      for (var q = 1; q <= 4; q++) {
        var qo = el('option', null, String(q));
        qo.value = String(q);
        if (q === state.qty) qo.selected = true;
        qty.appendChild(qo);
      }
      var price = el('span', 'lbg-row__price');
      var add = el('button', 'lbg-row__add', 'Add');
      add.type = 'button';
      controls.appendChild(select);
      controls.appendChild(qty);
      controls.appendChild(price);
      controls.appendChild(add);
      body.appendChild(controls);
      var status = el('p', 'lbg-row__status');
      status.setAttribute('role', 'status');
      body.appendChild(status);
      row.appendChild(body);

      function pickVariant() {
        var variants = product.colors[state.color].variants;
        var fit = size ? (SIZE_FIT[size] || [size]) : [];
        var chosen = null;
        for (var f = 0; f < fit.length && !chosen; f++) {
          chosen = variants.filter(function (v) {
            return v.available && v.opts.some(function (value) { return String(value).toLowerCase() === fit[f]; });
          })[0] || null;
        }
        state.variant = chosen || variants.filter(function (v) { return v.available; })[0] || variants[0];
      }

      function paint() {
        var entry = product.colors[state.color];
        thumb.src = entry.thumb;
        thumb.alt = '';
        if (chips) {
          renderChips(chips, product, state.color, 'fabric', function (picked) {
            state.color = picked;
            pickVariant();
            paint();
          }, usePhotos);
        }
        select.innerHTML = '';
        entry.variants.forEach(function (v) {
          var label = v.opts.length ? v.opts.join(' · ') : 'One size';
          var option = el('option', null, label + (v.available ? '' : ' (sold out)'));
          option.value = String(v.id);
          option.disabled = !v.available;
          if (state.variant && v.id === state.variant.id) option.selected = true;
          select.appendChild(option);
        });
        select.hidden = entry.variants.length < 2 && !(entry.variants[0] && entry.variants[0].opts.length);
        var colorText = hasColors(product) ? state.color : '';
        name.setAttribute('aria-label', (piece.name || product.title) + (colorText ? ', ' + colorText : ''));
        price.textContent = state.variant ? money(state.variant.cents) : '';
        add.disabled = !(state.variant && state.variant.available);
        add.textContent = add.disabled ? 'Sold Out' : 'Add';
        add.removeAttribute('data-label');
      }

      select.addEventListener('change', function () {
        var id = Number(select.value);
        state.variant = product.colors[state.color].variants.filter(function (v) { return v.id === id; })[0] || state.variant;
        paint();
      });
      qty.addEventListener('change', function () { state.qty = Number(qty.value) || 1; });
      add.addEventListener('click', function () {
        if (!state.variant) return;
        runAdd(add, status, [{ id: state.variant.id, quantity: state.qty }]);
      });

      pickVariant();
      paint();
      container.appendChild(row);
    });
    return shown;
  }

  /* ---------- Solids ---------- */

  var solids = data.solids || [];
  var solidStageNode = root.querySelector('[data-lbg-stage="solids"]');
  var solidFinish = root.querySelector('[data-lbg-finish="solids"]');
  var solidState = { index: 0, color: data.defaultColor };
  var solidButtons = Array.prototype.slice.call(root.querySelectorAll('[data-lbg-solid]'));

  function solidSize(stage) {
    var variant = stage.current();
    return variant ? sizeOf(variant.opts) : null;
  }

  function paintFinishes(stage) {
    if (!solidFinish) return;
    var solid = solids[solidState.index];
    var pieces = (data.finishes || []).filter(function (piece) {
      return piece.families.indexOf(solid.key) !== -1;
    });
    var shown = renderRows(solidFinish.querySelector('[data-finish-rows]'), pieces, stage.color, solidSize(stage));
    solidFinish.hidden = shown === 0;
    var title = solidFinish.querySelector('[data-finish-title]');
    if (title) title.setAttribute('data-for', solid.name);
  }

  function paintEdgeImages(color) {
    solidButtons.forEach(function (button) {
      var solid = solids[Number(button.getAttribute('data-lbg-solid'))];
      var img = button.querySelector('.lbg-edge__image');
      if (!solid || !img) return;
      var entry = solid.product.colors[color];
      if (entry && entry.own) swapImage(img, { src: entry.src, srcset: entry.srcset, alt: '' }, false);
    });
  }

  if (solidStageNode && solids.length) {
    var solidStage = new Stage(solidStageNode, {
      chipMode: 'fabric',
      onColor: function (name, stage) {
        solidState.color = name;
        paintEdgeImages(name);
        paintFinishes(stage);
      },
      onSelection: function (stage) { paintFinishes(stage); }
    });

    var selectSolid = function (index, scroll) {
      solidState.index = index;
      solidButtons.forEach(function (button) {
        var on = Number(button.getAttribute('data-lbg-solid')) === index;
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
        var card = button.closest('.lbg-edge');
        if (card) card.classList.toggle('is-selected', on);
      });
      var solid = solids[index];
      solidStage.show(solid.product, solidState.color, solid.product.title, '', scroll);
      solidState.color = solidStage.color;
      paintEdgeImages(solidState.color);
      paintFinishes(solidStage);
      if (scroll) {
        var top = solidStageNode.getBoundingClientRect().top;
        if (top > window.innerHeight * 0.6 || top < 0) {
          solidStageNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    solidButtons.forEach(function (button) {
      button.setAttribute('role', 'button');
      button.addEventListener('click', function (event) {
        event.preventDefault();
        selectSolid(Number(button.getAttribute('data-lbg-solid')), true);
      });
    });
    var startIndex = 0;
    var wantedSolid = (params.get('solid') || '').toLowerCase();
    var wantedColor = params.get('color');
    if (wantedSolid) {
      solids.forEach(function (solid, index) { if (solid.key === wantedSolid) startIndex = index; });
    }
    if (wantedColor) {
      var match = Object.keys(solids[startIndex].product.colors).filter(function (name) {
        return name.toLowerCase() === wantedColor.toLowerCase();
      })[0];
      if (match) solidState.color = match;
    }
    selectSolid(startIndex, false);
    if (wantedSolid || wantedColor) {
      window.requestAnimationFrame(function () {
        var section = root.querySelector('#lbg-solids');
        if (section) section.scrollIntoView({ block: 'start' });
      });
    }
  }

  /* ---------- Prints ---------- */

  var prints = data.prints || [];
  var grid = root.querySelector('[data-lbg-print-grid]');
  var panel = root.querySelector('[data-lbg-print-panel]');
  var printStage = panel ? new Stage(panel.querySelector('[data-lbg-stage="prints"]'), {
    chipMode: 'pill',
    onColor: function () { paintPrintPieces(); },
    onSelection: function () { paintPrintPieces(); }
  }) : null;
  var openPrint = -1;
  var printCards = grid ? Array.prototype.slice.call(grid.querySelectorAll('.lbg-print')) : [];

  function paintPrintPieces() {
    if (!panel || openPrint < 0) return;
    var finish = panel.querySelector('[data-lbg-finish="prints"]');
    var shown = renderRows(finish.querySelector('[data-finish-rows]'), prints[openPrint].pieces || [], printStage.color, solidSize(printStage), true);
    finish.hidden = shown === 0;
  }

  function placePanel(card) {
    var visible = printCards.filter(function (c) { return !c.hidden; });
    var top = card.offsetTop;
    var last = card;
    visible.forEach(function (c) { if (c.offsetTop === top) last = c; });
    last.parentNode.insertBefore(panel, last.nextSibling);
  }

  function closePrint() {
    if (!panel) return;
    panel.hidden = true;
    openPrint = -1;
    printCards.forEach(function (c) {
      c.classList.remove('is-open');
      var link = c.querySelector('[data-lbg-print]');
      if (link) link.setAttribute('aria-expanded', 'false');
    });
  }

  function openPrintAt(index, card) {
    var print = prints[index];
    if (!print || !panel) return;
    if (panel.hidden === false && openPrint === index) { closePrint(); return; }
    // Measure the row before the panel moves, so the panel's own height
    // never shifts the cards being measured.
    panel.hidden = true;
    placePanel(card);
    openPrint = index;
    printCards.forEach(function (c) {
      var on = c === card;
      c.classList.toggle('is-open', on);
      var link = c.querySelector('[data-lbg-print]');
      if (link) link.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    printStage.selection = [];
    printStage.show(print.product, null, print.name, print.line, true);
    paintPrintPieces();
    panel.hidden = false;
    var rect = panel.getBoundingClientRect();
    if (rect.top < 0 || rect.top > window.innerHeight * 0.55) {
      var header = document.querySelector('#shopify-section-header');
      var offset = header ? header.getBoundingClientRect().height : 0;
      window.scrollTo({ top: window.pageYOffset + rect.top - offset - 16, behavior: 'smooth' });
    }
  }

  printCards.forEach(function (card) {
    var link = card.querySelector('[data-lbg-print]');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.addEventListener('click', function (event) {
      event.preventDefault();
      openPrintAt(Number(link.getAttribute('data-lbg-print')), card);
    });
  });

  if (panel) {
    var closeButton = panel.querySelector('[data-lbg-print-close]');
    if (closeButton) closeButton.addEventListener('click', function () {
      var card = printCards.filter(function (c) { return c.classList.contains('is-open'); })[0];
      closePrint();
      if (card) {
        var link = card.querySelector('[data-lbg-print]');
        if (link) link.focus({ preventScroll: true });
      }
    });
    var resizeTimer = null;
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (openPrint < 0) return;
        var card = printCards.filter(function (c) { return c.classList.contains('is-open'); })[0];
        if (card) {
          panel.hidden = true;
          placePanel(card);
          panel.hidden = false;
        }
      }, 150);
    }, { passive: true });
  }

  var filters = Array.prototype.slice.call(root.querySelectorAll('[data-lbg-filter]'));
  filters.forEach(function (button) {
    button.addEventListener('click', function () {
      var value = button.getAttribute('data-lbg-filter');
      filters.forEach(function (b) {
        var on = b === button;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      closePrint();
      printCards.forEach(function (card) {
        card.hidden = !(value === 'all' || card.getAttribute('data-category') === value);
      });
    });
  });

  var wantedPrint = (params.get('print') || '').toLowerCase();
  if (wantedPrint && panel) {
    prints.forEach(function (print, index) {
      if (openPrint >= 0) return;
      if (print.product.handle === wantedPrint || String(print.name).toLowerCase() === wantedPrint) {
        var link = grid.querySelector('[data-lbg-print="' + index + '"]');
        if (link) openPrintAt(index, link.closest('.lbg-print'));
      }
    });
  }

  /* ---------- Swatches ---------- */

  var swatchPanel = root.querySelector('[data-lbg-swatch-panel]');
  if (swatchPanel && data.swatch) {
    var swatchState = { color: firstAvailableColor(data.swatch, data.defaultColor) };
    var swatchChips = swatchPanel.querySelector('[data-swatch-chips]');
    var swatchLabel = swatchPanel.querySelector('[data-swatch-color]');
    var swatchImage = root.querySelector('[data-swatch-image]');
    var swatchAdd = swatchPanel.querySelector('[data-swatch-add]');
    var swatchStatus = swatchPanel.querySelector('[data-swatch-status]');
    var swatchBundle = swatchPanel.querySelector('[data-swatch-bundle]');
    var paintSwatch = function (eager) {
      var entry = data.swatch.colors[swatchState.color];
      swatchLabel.textContent = swatchState.color;
      swapImage(swatchImage, entry, eager);
      renderChips(swatchChips, data.swatch, swatchState.color, 'fabric', function (name) {
        swatchState.color = name;
        paintSwatch(true);
      });
      var variant = entry.variants.filter(function (v) { return v.available; })[0];
      swatchAdd.disabled = !variant;
      swatchAdd.removeAttribute('data-label');
      swatchAdd.textContent = variant ? 'Add Swatch · ' + money(variant.cents) : 'Sold Out';
    };
    swatchAdd.addEventListener('click', function () {
      var variant = data.swatch.colors[swatchState.color].variants.filter(function (v) { return v.available; })[0];
      if (variant) runAdd(swatchAdd, swatchStatus, [{ id: variant.id, quantity: 1 }], 'A ' + swatchState.color + ' swatch is in your cart.');
    });
    if (swatchBundle) swatchBundle.addEventListener('click', function () {
      runAdd(swatchBundle, swatchStatus, [{ id: Number(swatchBundle.getAttribute('data-swatch-bundle')), quantity: 1 }], 'The swatch bundle is in your cart.');
    });
    paintSwatch(false);
  }
})();
